import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User } from 'lucide-react';
import BenevolCheckLogo from '../components/BenevolCheckLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [pendingCertificates, setPendingCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCertificates();
    if (user?.role === 'association_admin') {
      fetchPendingCertificates();
    }
  }, [user]);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/certificates`, {
        headers: getAuthHeader()
      });
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCertificates = async () => {
    try {
      const response = await axios.get(`${API}/certificates/pending/all`, {
        headers: getAuthHeader()
      });
      setPendingCertificates(response.data);
    } catch (error) {
      console.error('Error fetching pending certificates:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'validated':
        return <Badge className="bg-green-500" data-testid="status-validated">✅ Validée</Badge>;
      case 'pending_validation':
        return <Badge className="bg-yellow-500" data-testid="status-pending">⌛ En attente</Badge>;
      default:
        return <Badge data-testid="status-draft">Brouillon</Badge>;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <BenevolCheckLogo size="sm" showText={false} />
              <h1 className="text-base sm:text-xl font-bold text-indigo-900">Bénévol'Check</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
              <span className="text-sm sm:text-base text-gray-700 truncate flex-1 sm:flex-initial" data-testid="user-name">
                {user?.name}
              </span>
              <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} data-testid="profile-button">
                <User size={18} />
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button">
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-indigo-900 mb-2" data-testid="dashboard-title">
            {user?.role === 'volunteer' ? 'Mes attestations' : 'Gestion des attestations'}
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            {user?.role === 'volunteer' 
              ? 'Créez et gérez vos attestations de compétences bénévoles'
              : `Association : ${user?.association_name}`
            }
          </p>
        </div>

        {/* Create Button */}
        <div className="mb-6">
          <Button 
            onClick={() => navigate('/create-certificate')} 
            size="lg"
            data-testid="create-certificate-button"
            className="w-full sm:w-auto"
          >
            📝 Créer une nouvelle attestation
          </Button>
        </div>

        {/* Tabs for Association Admins */}
        {user?.role === 'association_admin' ? (
          <Tabs defaultValue="all" className="w-full">
            <TabsList data-testid="tabs-list">
              <TabsTrigger value="all" data-testid="tab-all">Toutes les attestations</TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">
                En attente de validation ({pendingCertificates.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="all">
              <CertificatesList 
                certificates={certificates} 
                navigate={navigate} 
                getStatusBadge={getStatusBadge}
              />
            </TabsContent>
            
            <TabsContent value="pending">
              <CertificatesList 
                certificates={pendingCertificates} 
                navigate={navigate} 
                getStatusBadge={getStatusBadge}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <CertificatesList 
            certificates={certificates} 
            navigate={navigate} 
            getStatusBadge={getStatusBadge}
          />
        )}
      </main>
    </div>
  );
};

const CertificatesList = ({ certificates, navigate, getStatusBadge }) => {
  if (certificates.length === 0) {
    return (
      <Card data-testid="empty-state">
        <CardContent className="py-12 text-center">
          <p className="text-gray-500 text-base sm:text-lg">Aucune attestation pour le moment</p>
          <p className="text-gray-400 text-sm sm:text-base mt-2">Créez votre première attestation pour commencer</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-3 sm:gap-4" data-testid="certificates-list">
      {certificates.map((cert) => (
        <Card 
          key={cert.id} 
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate(`/certificate/${cert.id}`)}
          data-testid={`certificate-card-${cert.id}`}
        >
          <CardHeader className="pb-3 sm:pb-4">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-0">
              <div className="flex-1 w-full">
                <CardTitle className="text-base sm:text-lg mb-2" data-testid="certificate-volunteer-name">
                  {cert.volunteer_name}
                </CardTitle>
                <div className="space-y-1 text-sm sm:text-base">
                  <p className="text-gray-600 flex items-start" data-testid="certificate-association">
                    <span className="mr-1">🏛️</span>
                    <span className="flex-1">{cert.association_name}</span>
                  </p>
                  <p className="text-gray-600 flex items-start" data-testid="certificate-mission">
                    <span className="mr-1">💼</span>
                    <span className="flex-1">{cert.mission_description}</span>
                  </p>
                  <p className="text-gray-600 text-xs sm:text-sm" data-testid="certificate-dates">
                    📅 Du {new Date(cert.start_date).toLocaleDateString('fr-FR')} au {new Date(cert.end_date).toLocaleDateString('fr-FR')}
                  </p>
                  <p className="text-sm sm:text-base font-semibold text-indigo-600 mt-1" data-testid="certificate-hours">
                    ⏱️ {cert.hours_volume} heures
                  </p>
                </div>
              </div>
              <div className="w-full sm:w-auto flex justify-end">
                {getStatusBadge(cert.status)}
              </div>
            </div>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
};

export default DashboardPage;